import { useMutation } from '@apollo/client'
import type {
  Product,
  ProductOption,
  ProductVariant,
} from '@commerce/types/product'
import cartLinesAddMutation, {
  cartLinesAddVars,
} from '@commerce/utils/mutations/cart-lines-add'
import { Info } from '@components/icons'
import { Button, Quantity, useUI } from '@components/ui'
import WYSIWYG from '@components/ui/WYSIWYG'
import { BundleInfo } from '@lib/helpers/general'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { memo, useState } from 'react'
import { SelectedOptions } from '../helpers'
import BundleProductOptionItem from './BundleProductOptionItem'
interface BundleProductOptionsProps {
  product: Product
  bundleInfo: BundleInfo[]
  variants: ProductVariant[]

  selectedOptions: SelectedOptions
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
  prismicContent: any
  currencyCode?: string
  disabled?: boolean
}

const BundleProductOptionsSelectBox: React.FC<BundleProductOptionsProps> = ({
  product,
  bundleInfo,
  variants,
  selectedOptions,
  setSelectedOptions,
  prismicContent,
  currencyCode,
  disabled,
}) => {
  const { locale } = useRouter()
  const { t } = useTranslation('common')
  const { openCartNotification, closeCartNotification } = useUI()
  // const { hits, sendEvent } = useHits()
  const [coverage, setCoverage] = useState<
    [number | null, string | null] | null
  >(null)
  const [loading, setLoading] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [variant, setVariant] = useState<ProductVariant | null>(null)
  const handleQtyIncrease = () => setQuantity(quantity + 1)
  const handleQtyDecrease = () => setQuantity(quantity - 1)

  const convertOptions = (options: ProductOption[]): SelectedOptions => {
    const convertedOptions: SelectedOptions = {}
    options.forEach((option) => {
      const displayName = option.displayName
      const valueLabel = option.values[0].label.toLowerCase()
      convertedOptions[displayName] = valueLabel
    })
    return convertedOptions
  }
  const { declarationText } = prismicContent || {}
  const extractNumberWithUnit = (
    text: string
  ): [number | null, string | null] => {
    const pattern: RegExp = /(\d+(\.\d+)?)(\s*[a-zA-Z²]+)/
    const match: RegExpExecArray | null = pattern.exec(text)
    if (match) {
      const number: number = parseFloat(match[1])
      const unit: string = match[3].trim()
      return [number, unit]
    } else {
      return [null, null]
    }
  }

  const [addItem] = useMutation(cartLinesAddMutation, {
    onCompleted() {
      openCartNotification(`${quantity} ${product.name} ${t('added-to-cart')}`)
      setLoading(false)
      // sendEvent('conversion', hits, 'Added To Cart', {
      //   eventSubtype: 'addToCart',
      //   product: {
      //     currency: product?.price?.currencyCode,
      //     product: product?.name,
      //     productId: product?.id,
      //     variant: variant?.name,
      //     variantId: variant?.id,
      //     sku: variant?.sku,
      //     price: variant?.price,
      //     quantity: quantity,
      //   },
      // })
    },
    onError() {
      setLoading(false)
    },
  })
  const addToCart = async () => {
    setLoading(true)
    addItem({
      variables: cartLinesAddVars({
        locale,
        variantId: variant?.id.toString()!,
        quantity,
      }),
      context: { locale },
    })
  }
  return (
    <div className="flex h-full flex-1 flex-col gap-4">
      <div className="body-1-bold">{t('pick-a-size')}</div>
      {variants.map((variant) => {
        const convertedOptions = convertOptions(variant.options)
        const bundleOptionItem = bundleInfo.find(
          (bundle) => bundle.variant === variant.id
        )
        const selected =
          JSON.stringify(convertedOptions) == JSON.stringify(selectedOptions)
        const onClick = () => {
          setVariant(variant)
          setSelectedOptions(convertedOptions)
          setCoverage(extractNumberWithUnit(bundleOptionItem?.name!))
        }

        return (
          <div key={variant.id} onClick={onClick}>
            <BundleProductOptionItem
              bundleOptionItem={bundleOptionItem}
              price={variant.price}
              currencyCode={currencyCode!}
              selected={selected}
            />
          </div>
        )
      })}
      <div className="mt-auto flex flex-col ">
        <div className="flex items-center justify-center gap-1  pb-4">
          <div className="body-2 mt-auto text-center text-dark-grey">
            {t('your-selection-covers')}
            <span className="body-2-bold">
              &nbsp;{coverage?.[0] ? coverage[0] * quantity : 0}
              {coverage?.[1] ? coverage[1] : 'm²'}&nbsp;
            </span>
            {t('with-two-coats')}
          </div>
          <div className="cursor-pointer">
            <div className="group relative flex flex-col items-center">
              <Info />
              <div className="body-2 absolute top-5 -right-18 hidden w-[280px] rounded-sm bg-white p-3 text-xs leading-none shadow-[0_2px_4px_rgba(100,100,100,0.1)] group-hover:flex">
                <WYSIWYG content={declarationText} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Quantity
            value={quantity}
            increase={handleQtyIncrease}
            decrease={handleQtyDecrease}
            classNames="bg-white border border-ui-grey-25 min-w-32 justify-around"
          />
          <Button
            aria-label={`${t('add-to-cart')}`}
            type="button"
            width="100%"
            onClick={addToCart}
            loading={loading}
            disabled={disabled}
          >
            {t('make-your-selection')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default memo(BundleProductOptionsSelectBox)
BundleProductOptionsSelectBox
